import * as React from 'react';
import { AsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryTypes';
import {
  createAsyncBoundaryInjectorParams,
  AsyncBoundaryContext,
} from 'utils/asyncBoundary/asyncBoundaryUtils';
import { asyncTimeout } from 'utils/asyncTimeout';

type Props = {
  fallback: React.ReactNode;
  minimalDelay?: number;
};

export const AsyncBoundaryComponent: React.FC<Props> = (props) => {
  const { children, fallback, minimalDelay = 800 } = props;

  const willUnmountRef = React.useRef(false);

  const [active, setActive] = React.useState(false);

  const [promises, setPromises] = React.useState<Promise<void>[]>([]);

  const [fallbackPromises, setFallbackPromises] = React.useState<
    Promise<void>[]
  >([]);

  const [error, setError] = React.useState<null | Error>(null);

  React.useLayoutEffect(() => {
    return () => {
      willUnmountRef.current = true;
    };
  }, []);

  React.useEffect(() => {
    let canceled = false;

    if (promises.length > 0) {
      Promise.all([asyncTimeout(minimalDelay), ...promises])
        .catch((error) => {
          if (!canceled && !willUnmountRef.current) {
            setError(error);
          }
        })
        .finally(() => {
          if (!canceled && !willUnmountRef.current) {
            setPromises([]);
          }
        });
    }

    return () => {
      canceled = true;
    };
  }, [minimalDelay, promises]);

  React.useEffect(() => {
    let canceled = false;

    if (fallbackPromises.length > 0) {
      Promise.all([asyncTimeout(minimalDelay), ...fallbackPromises])
        .catch((error) => {
          if (!canceled && !willUnmountRef.current) {
            setError(error);
          }
        })
        .finally(() => {
          if (!canceled && !willUnmountRef.current) {
            setFallbackPromises([]);
          }
        });
    }

    return () => {
      canceled = true;
    };
  }, [fallbackPromises, minimalDelay]);

  if (error) {
    throw error;
  }

  const injectPromise = React.useCallback(
    (promise, params = createAsyncBoundaryInjectorParams()) => {
      const { shouldShowFallback } = params;

      if (shouldShowFallback) {
        setFallbackPromises((promises) => {
          return [...promises, promise];
        });
      } else {
        setPromises((promises) => {
          return [...promises, promise];
        });
      }
    },
    []
  );

  React.useLayoutEffect(() => {
    const active = promises.length > 0 || fallbackPromises.length > 0;

    setActive(active);
  }, [fallbackPromises.length, promises.length]);

  const asyncBoundary = React.useMemo<AsyncBoundary>(
    () => ({ injectPromise, active }),
    [active, injectPromise]
  );

  const shouldShowFallback = fallbackPromises.length > 0;

  return (
    <AsyncBoundaryContext.Provider value={asyncBoundary}>
      {shouldShowFallback ? fallback : children}
    </AsyncBoundaryContext.Provider>
  );
};
